import { useEffect, useState } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Sparkles, FolderOpenDot, MessageSquareQuote, Newspaper, BadgeDollarSign, HelpCircle, Inbox, MailPlus, Settings, LogOut } from 'lucide-react';
import { supabase, isAdmin } from '@/lib/supabase';
import BrandMark from '@/components/site/BrandMark';
import { toast } from 'sonner';

const links = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/services', icon: Sparkles, label: 'Services' },
  { to: '/admin/projects', icon: FolderOpenDot, label: 'Portfolio' },
  { to: '/admin/testimonials', icon: MessageSquareQuote, label: 'Testimonials' },
  { to: '/admin/posts', icon: Newspaper, label: 'Blog' },
  { to: '/admin/pricing', icon: BadgeDollarSign, label: 'Pricing' },
  { to: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
  { to: '/admin/leads', icon: Inbox, label: 'Leads' },
  { to: '/admin/newsletter', icon: MailPlus, label: 'Newsletter' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { nav('/admin/login'); return; }
      setUser(data.user);
      const ok = await isAdmin();
      setAdmin(ok);
      setLoading(false);
    })();
  }, [nav]);

  const signOut = async () => { await supabase.auth.signOut(); toast.success('Signed out'); nav('/admin/login'); };

  if (loading) return <div className="grid min-h-screen place-items-center text-ink-500">Loading…</div>;

  return (
    <div className="flex min-h-screen bg-ink-50">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-ink-100 bg-white px-5 py-6">
        <Link to="/" className="flex items-center"><BrandMark size="md" /></Link>
        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ink-400">Studio CMS</p>
        <nav className="mt-10 flex-1 space-y-1">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase()}`} className={({isActive}) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] transition ${isActive ? 'bg-cyan-soft text-cyan-700' : 'text-ink-700 hover:bg-ink-50'}`}>
              <l.icon className="h-4 w-4" />
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 rounded-2xl border border-ink-100 bg-ink-50/50 p-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink-400">Signed in</p>
          <p className="mt-1 truncate text-[13px] text-ink-900">{user?.email}</p>
          <p className="mt-1 text-[11px] text-ink-500">{admin ? 'Admin' : 'No admin role (read-only)'}</p>
          <button onClick={signOut} data-testid="admin-signout" className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-ink-700 hover:text-cyan"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {!admin && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-sm text-amber-800">
            You&apos;re signed in but don&apos;t have the <code>admin</code> role yet. Run the SQL on <code>/app/supabase_schema.sql</code> and then: <code>update profiles set role=&apos;admin&apos; where email=&apos;{user?.email}&apos;;</code>
          </div>
        )}
        <div className="px-6 py-8 md:px-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
